import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, Container, Grid, Typography, Card } from '@mui/material';
import useSettings from '../../../../hooks/useSettings';
import Page from '../../../../components/Page';
import DataFilter from './components/filter';
import { useSelector } from 'react-redux';
import moment from 'moment/moment';
import { LineChart } from '@mui/x-charts/LineChart';
import DataGridTable from '../../../../components/table/DataGridTable';
import { capitalize } from '../../../../utils/common';

const PurchaseAxisTooltip = ({ dataIndex, series, pointMetadata }) => {
  if (dataIndex == null) {
    return null;
  }

  const points = series
    .map(({ id }) => pointMetadata?.[id]?.[dataIndex])
    .filter((point) => point && Number.isFinite(Number(point.costPerUnit)));

  if (points.length === 0) {
    return null;
  }

  const { grnId, receivedAt } = points[0];

  return (
    <Box sx={{ bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, boxShadow: 3, p: 1.5 }}>
      <Typography variant="subtitle2">{`GRN-${grnId}`}</Typography>
      <Typography variant="body2" sx={{ mb: 0.5 }}>
        {moment(receivedAt).format('YYYY-MM-DD HH:mm:ss')}
      </Typography>
      {points.map((point) => (
        <Box key={point.foodId} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2">{point.foodName}</Typography>
          <Typography variant="body2">
            {`${Number(point.costPerUnit).toFixed(2)}${point.unitSymbol ? ` / ${point.unitSymbol}` : ''}`}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const PurchasingDashboard = () => {
  const { themeStretch } = useSettings();
  const [originalData, setOriginalData] = useState([]);
  const [data, setData] = useState([]);
  const [reorderData, setReorderData] = useState([]);
  let selectedLocation;
  ({ selectedLocation } = useSelector((state) => state.location));

  const value = useMemo(() => {
    const grnMap = new Map();
    const foodMap = new Map();
    const pointMap = new Map();

    data.forEach((item) => {
      const grnId = String(item.grnId);
      const foodId = String(item.foodId);
      const currentGrn = grnMap.get(grnId);

      if (!currentGrn || moment(item.receivedAt).isBefore(moment(currentGrn.receivedAt))) {
        grnMap.set(grnId, { grnId, receivedAt: item.receivedAt });
      }

      foodMap.set(foodId, item.foodName);
      pointMap.set(`${foodId}:${grnId}`, {
        ...item,
        grnId,
        foodId,
        costPerUnit: Number(item.costPerUnit)
      });
    });

    const grnSlots = Array.from(grnMap.values()).sort((a, b) => {
      const dateDifference = moment(a.receivedAt).diff(moment(b.receivedAt));
      return dateDifference || Number(a.grnId) - Number(b.grnId);
    });
    const pointMetadata = {};
    const series = Array.from(foodMap.entries()).map(([foodId, foodName]) => {
      const seriesId = `food-${foodId}`;
      const points = grnSlots.map(({ grnId }) => pointMap.get(`${foodId}:${grnId}`) || null);

      pointMetadata[seriesId] = points;

      return {
        id: seriesId,
        label: foodName,
        data: points.map((point) => (point && Number.isFinite(point.costPerUnit) ? point.costPerUnit : null)),
        connectNulls: true,
        showMark: true,
        valueFormatter: (cost) => (cost === null ? '' : Number(cost).toFixed(2))
      };
    });

    return {
      xAxis: grnSlots.map(({ grnId }) => grnId),
      axisDates: Object.fromEntries(grnSlots.map(({ grnId, receivedAt }) => [grnId, receivedAt])),
      pointMetadata,
      series
    };
  }, [data]);

  const customize = {
    height: 700,
    legend: { hidden: false },
    margin: { top: 20, bottom: 50, left: 50, right: 20 }
  };

  const columns = reorderData?.[0]
    ? Object.keys(reorderData?.[0])
      .map((value) => {
        if (['foodId', 'locationId'].includes(value)) {
          return null;
        } else if (value === 'status') {
          return {
            accessorKey: 'status',
            header: 'Status',
            Cell: ({ row }) => {
              return row?.original?.status && <Chip key={value} label={row?.original?.status} color={'error'} />;
            }
          };
        } else {
          return {
            accessorKey: value,
            header: capitalize(value)
          };
        }
      })
      .filter(Boolean)
    : [];

  const buildOrderSummary = (rows) => {
    const map = new Map();
    rows?.forEach((r) => {
      const required = Math.max(0, Number(r?.maxQty || 0) - Number(r?.totalStock || 0));
      const id = r.foodId;
      const sym = r?.symbol || '';
      const minQ = Number(r?.reorderQty ?? r?.minQty ?? 0);
      const maxQ = Number(r?.maxQty ?? 0);
      if (!map.has(id)) map.set(id, { foodId: id, foodName: r.foodName, symbol: sym, totalRequired: 0, minQty: Number.isFinite(minQ) ? minQ : 0, maxQty: Number.isFinite(maxQ) ? maxQ : 0 });
      const cur = map.get(id);
      const sum = Number(cur.totalRequired || 0) + Number(required || 0);
      cur.totalRequired = parseFloat(sum.toFixed(2));
      if (!cur.symbol && sym) cur.symbol = sym;
      if (Number.isFinite(minQ)) cur.minQty = cur.minQty === 0 ? minQ : Math.min(cur.minQty, minQ);
      if (Number.isFinite(maxQ)) cur.maxQty = Math.max(cur.maxQty || 0, maxQ);
      map.set(id, cur);
    });
    return Array.from(map.values())
      .filter((x) => Number(x.totalRequired) > 0)
      .sort((a, b) => Number(b.totalRequired) - Number(a.totalRequired));
  };

  const summaryData = useMemo(() => buildOrderSummary(reorderData), [reorderData]);

  const summaryColumns = [
    { accessorKey: 'foodName', header: 'Food Name' },
    {
      accessorKey: 'minQty',
      header: 'Min Qty',
      Cell: ({ row }) => {
        const n = Number(row?.original?.minQty || 0);
        const sym = row?.original?.symbol || '';
        return `${n.toFixed(2)} ${sym}`.trim();
      }
    },
    {
      accessorKey: 'maxQty',
      header: 'Max Qty',
      Cell: ({ row }) => {
        const n = Number(row?.original?.maxQty || 0);
        const sym = row?.original?.symbol || '';
        return `${n.toFixed(2)} ${sym}`.trim();
      }
    },
    {
      accessorKey: 'totalRequired',
      header: 'Total Required',
      Cell: ({ row }) => {
        const n = Number(row?.original?.totalRequired || 0);
        const sym = row?.original?.symbol || '';
        return `${n.toFixed(2)} ${sym}`.trim();
      }
    }
  ];

  return (
    <Page title="Purchasing Dashboard">
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <Grid container spacing={2}>
          <DataFilter data={data} setData={setData} setReorderData={setReorderData} setOriginalData={setOriginalData} />
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Purchasing Trend by Food (Cost Per Unit)
              </Typography>
              <LineChart
                xAxis={[
                  {
                    scaleType: 'point',
                    data: value.xAxis,
                    label: 'GRN Receipt Date',
                    valueFormatter: (grnId) => moment(value.axisDates[grnId]).format('YYYY-MM-DD')
                  }
                ]}
                yAxis={[{ label: 'Cost Per Unit' }]}
                series={value.series}
                tooltip={{ trigger: 'axis' }}
                slots={{ axisContent: PurchaseAxisTooltip }}
                slotProps={{ axisContent: { pointMetadata: value.pointMetadata } }}
                {...customize}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Reorder List
              </Typography>
              <DataGridTable
                name={'Reorder List'}
                data={reorderData}
                column={columns}
                onRowClick={() => {}}
                isRowClickable={false}
                isLoading={false}
                setRowSelection={() => {}}
                enableRowSelection={false}
                enableRowActions={false}
                renderRowActionItems={() => []}
              />
            </Card>
          </Grid>
          <Grid item xs={12} md={12}>
            <Card sx={{ p: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Total Required To Order (All Locations)
              </Typography>
              <DataGridTable
                name={'Total Required To Order'}
                data={summaryData}
                column={summaryColumns}
                onRowClick={() => {}}
                isRowClickable={false}
                isLoading={false}
                setRowSelection={() => {}}
                enableRowSelection={false}
                enableRowActions={false}
                renderRowActionItems={() => []}
              />
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default PurchasingDashboard;
